import { ClientProps } from '@/types';

export interface SelectedBrandPairLike {
  codLoja?: number | string | null;
  codSegmento?: number | string | null;
}

export const getSegmentCode = (segmento: any): number =>
  Number(segmento?.cod_segmento ?? segmento?.COD_SEGMENTO ?? segmento);

/**
 * Re-escopa o `selectedClient` para a loja+segmento ativos de `selectedBrandPair`,
 * validando contra `selectedClient.lojas`.
 *
 * Regras (em ordem):
 * 1. Se não houver brand pair ativo, devolve o `selectedClient` original.
 * 2. Se o pair bate com uma loja em `selectedClient.lojas`, devolve o cliente
 *    com `COD_LOJA`/`COD_TABELA_VENDA`/etc. da loja matched e
 *    `COD_SEGMENTO = [segmentoAtivo]`.
 * 3. Fallback legado: cliente sem o array `lojas` hidratado — só aceita se o
 *    `COD_LOJA`/`COD_SEGMENTO` raiz do cliente bater com o pair.
 * 4. Caso contrário (cliente tem `lojas`, mas o pair não está em nenhuma delas):
 *    retorna `null`. Os consumidores devem tratar como "combinação inválida"
 *    e evitar carregar/calcular dados da loja errada silenciosamente.
 */
export const buildScopedClient = (
  selectedClient: ClientProps | null | undefined,
  selectedBrandPair: SelectedBrandPairLike | null | undefined
): ClientProps | null => {
  if (!selectedClient) return null;
  if (!selectedBrandPair?.codLoja || !selectedBrandPair?.codSegmento) {
    return selectedClient as ClientProps;
  }

  const codLojaAtiva = Number(selectedBrandPair.codLoja);
  const codSegmentoAtivo = Number(selectedBrandPair.codSegmento);
  if (!Number.isFinite(codLojaAtiva) || !Number.isFinite(codSegmentoAtivo)) {
    return selectedClient as ClientProps;
  }

  const lojas = Array.isArray(selectedClient.lojas) ? selectedClient.lojas : [];

  const matchedLoja = lojas.find((loja: any) => {
    if (Number(loja?.COD_LOJA) !== codLojaAtiva) return false;
    const segmentosLoja = Array.isArray(loja?.COD_SEGMENTO) ? loja.COD_SEGMENTO : [];
    return segmentosLoja.some((s: any) => getSegmentCode(s) === codSegmentoAtivo);
  });

  if (matchedLoja) {
    const segmentosLoja = Array.isArray(matchedLoja?.COD_SEGMENTO) ? matchedLoja.COD_SEGMENTO : [];
    const segmentoAtivo = segmentosLoja.find((s: any) => getSegmentCode(s) === codSegmentoAtivo);
    const payLoja = matchedLoja?.COD_PAGAMENTO;
    const payCliente = selectedClient?.COD_PAGAMENTO;
    const COD_PAGAMENTO =
      Array.isArray(payLoja) && payLoja.length > 0
        ? payLoja
        : Array.isArray(payCliente) && payCliente.length > 0
          ? payCliente
          : Array.isArray(payLoja)
            ? payLoja
            : Array.isArray(payCliente)
              ? payCliente
              : [];

    return {
      ...selectedClient,
      ...matchedLoja,
      COD_SEGMENTO: segmentoAtivo ? [segmentoAtivo] : segmentosLoja,
      COD_PAGAMENTO,
    } as ClientProps;
  }

  if (lojas.length === 0) {
    if (Number(selectedClient.COD_LOJA) !== codLojaAtiva) return null;
    const segmentosCliente = Array.isArray(selectedClient.COD_SEGMENTO) ? selectedClient.COD_SEGMENTO : [];
    const segmentoAtivo = segmentosCliente.find((s: any) => getSegmentCode(s) === codSegmentoAtivo);
    if (!segmentoAtivo) return null;

    return {
      ...selectedClient,
      COD_SEGMENTO: [segmentoAtivo],
    } as ClientProps;
  }

  return null;
};
